// Client API layer v0.4.1 - Thin wrapper, no auth logic
// Calls Cloud Function which handles all validation/transactions

import { auth, functions } from "./firebase";
import { httpsCallable, type HttpsCallableResult } from "firebase/functions";
import type { ResidentParkingOnboarding } from "./content/residentParkingOnboarding";

console.log("API_MODULE_LOADED", {
  authInstance: auth.app.name,
  functionsInstance: functions.app.name,
  sameInstance: auth.app.name === functions.app.name,
});

export type NewVehiclePayload = {
  licensePlate: string;
  make: string;
  model: string;
  color?: string;
};

export interface CreateVehicleResponse {
  ok: boolean;
  data?: {
    vehicleId: string;
    linkId: string;
    vehicle: {
      id: string;
      licensePlate: string;
      make: string;
      model: string;
      color: string;
      active: boolean;
      isPrimary: boolean;
    };
  };
  error?: {
    code: string;
    message: string;
  };
}

export interface UpdateVehicleResponse {
  ok: boolean;
  data?: {
    vehicleId: string;
  };
  error?: {
    code: string;
    message: string;
  };
}

export interface DeleteVehicleResponse {
  ok: boolean;
  error?: {
    code: string;
    message: string;
  };
}

// Car wash booking types
export interface Service {
  id: string;
  name: string;
  description?: string;
  priceCents: number;
  durationMinutes: number;
}

export interface TimeSlot {
  slotId: string;
  startTime: string;
  endTime: string;
  display: string;
  available: boolean;
  capacityTotal: number;
  capacityRemaining: number;
}

export interface GetAvailableSlotsResponse {
  ok: boolean;
  date?: string;
  dayName?: string;
  slots?: TimeSlot[];
  service?: Service;
  schedule?: any;
  error?: {
    code: string;
    message: string;
  };
}

export interface BookingResponse {
  ok: boolean;
  requestId?: string;
  status?: string;
  vehicleInfo?: {
    licensePlate: string;
    make: string;
    model: string;
  };
  serviceInfo?: {
    name: string;
    priceCents: number;
  };
  startTime?: string;
  endTime?: string;
  error?: {
    code: string;
    message: string;
  };
}

export interface Booking {
  id: string;
  startTime: string;
  endTime: string;
  serviceId: string;
  serviceName: string;
  serviceDuration: number;
  priceCents: number;
  status: string;
  vehicleInfo: {
    licensePlate: string;
    make: string;
    model: string;
    color?: string;
  };
  createdAt?: string;
}

export interface GetBookingsResponse {
  ok: boolean;
  bookings?: Booking[];
  error?: {
    code: string;
    message: string;
  };
}

async function createResidentVehicle(
  payload: NewVehiclePayload
): Promise<CreateVehicleResponse> {
  // Normalize plate before sending
  const normalizedPlate = payload.licensePlate
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");

  const normalizedPayload: NewVehiclePayload = {
    ...payload,
    licensePlate: normalizedPlate,
  };

  console.log("API_CALL_MODE", {
    usingCallable: true,
    currentUserUid: auth.currentUser?.uid || null,
    currentUserEmail: auth.currentUser?.email || null
  });

  console.log("API_PLATE_NORMALIZATION", {
    original: payload.licensePlate,
    normalized: normalizedPayload.licensePlate,
  });

  const user = auth.currentUser;
  if (!user) {
    console.error("API_NO_CURRENT_USER");
    throw new Error("No authenticated Firebase user at submit time");
  }

  const token = await user.getIdToken(true);
  console.log("API_ID_TOKEN_OK", {
    uid: user.uid,
    email: user.email,
    tokenPrefix: token?.slice(0, 12) || null
  });

  console.log("API_CALL_START", { payload: normalizedPayload });

  try {
    const createVehicleFn = httpsCallable(functions, "createResidentVehicle");
    console.log("API_CALLABLE_FN_CREATED");

    const result: HttpsCallableResult<any> = await createVehicleFn(normalizedPayload);
    console.log("API_CALL_RESULT", result);

    if (!result.data || !result.data.ok) {
      const errorMsg = result.data?.error?.message || "Unable to add vehicle.";
      console.error("API_ERROR_RESPONSE", result.data);
      throw new Error(errorMsg);
    }

    return result.data as CreateVehicleResponse;
  } catch (error: any) {
    console.error("API_CALL_ERROR", error);
    console.error("API_ERROR_CODE", error.code);
    console.error("API_ERROR_MESSAGE", error.message);
    throw error;
  }
}

async function updateResidentVehicle(
  payload: { vehicleId: string; licensePlate: string; make: string; model: string; color?: string }
): Promise<UpdateVehicleResponse> {
  const user = auth.currentUser;
  if (!user) {
    console.error("API_NO_CURRENT_USER");
    throw new Error("No authenticated Firebase user");
  }

  const token = await user.getIdToken(true);
  console.log("API_UPDATE_VEHICLE_START", { payload });

  try {
    const updateVehicleFn = httpsCallable(functions, "updateResidentVehicle");
    const result: HttpsCallableResult<any> = await updateVehicleFn(payload);
    console.log("API_UPDATE_VEHICLE_RESULT", result);

    if (!result.data || !result.data.ok) {
      const errorMsg = result.data?.error?.message || "Unable to update vehicle.";
      console.error("API_ERROR_RESPONSE", result.data);
      throw new Error(errorMsg);
    }

    return result.data as UpdateVehicleResponse;
  } catch (error: any) {
    console.error("API_UPDATE_VEHICLE_ERROR", error);
    throw error;
  }
}

async function deleteResidentVehicle(vehicleId: string): Promise<DeleteVehicleResponse> {
  const user = auth.currentUser;
  if (!user) {
    console.error("API_NO_CURRENT_USER");
    throw new Error("No authenticated Firebase user");
  }

  const token = await user.getIdToken(true);
  console.log("API_DELETE_VEHICLE_START", { vehicleId });

  try {
    const deleteVehicleFn = httpsCallable(functions, "deleteResidentVehicle");
    const result: HttpsCallableResult<any> = await deleteVehicleFn({ vehicleId });
    console.log("API_DELETE_VEHICLE_RESULT", result);

    if (!result.data || !result.data.ok) {
      const errorMsg = result.data?.error?.message || "Unable to remove vehicle.";
      console.error("API_ERROR_RESPONSE", result.data);
      throw new Error(errorMsg);
    }

    return result.data as DeleteVehicleResponse;
  } catch (error: any) {
    console.error("API_DELETE_VEHICLE_ERROR", error);
    throw error;
  }
}

// Get available time slots for car wash
async function getAvailableCarWashSlots(
  serviceId: string,
  date: string
): Promise<GetAvailableSlotsResponse> {
  const user = auth.currentUser;
  if (!user) {
    console.error("API_NO_CURRENT_USER");
    throw new Error("No authenticated Firebase user");
  }

  const token = await user.getIdToken(true);
  console.log("API_GET_SLOTS_START", { serviceId, date });

  try {
    const getSlotsFn = httpsCallable(functions, "getAvailableSlots");
    const result: HttpsCallableResult<any> = await getSlotsFn({ serviceId, date });
    console.log("API_GET_SLOTS_RESULT", result);

    if (!result.data || !result.data.ok) {
      const errorMsg = result.data?.error?.message || "Unable to fetch available slots.";
      console.error("API_ERROR_RESPONSE", result.data);
      throw new Error(errorMsg);
    }

    return result.data as GetAvailableSlotsResponse;
  } catch (error: any) {
    console.error("API_GET_SLOTS_ERROR", error);
    throw error;
  }
}

// Create car wash booking
async function createCarWashBooking(
  vehicleId: string,
  serviceId: string,
  slotId: string,
  startTime: string,
  endTime: string,
  serviceDuration: number,
  priceCents: number,
  notes?: string
): Promise<BookingResponse> {
  const user = auth.currentUser;
  if (!user) {
    console.error("API_NO_CURRENT_USER");
    throw new Error("No authenticated Firebase user");
  }

  const token = await user.getIdToken(true);
  console.log("API_CREATE_BOOKING_START", {
    vehicleId,
    serviceId,
    slotId,
    startTime,
    endTime,
  });

  try {
    const holdAppointmentFn = httpsCallable(functions, "holdCarWashAppointment");
    const result: HttpsCallableResult<any> = await holdAppointmentFn({
      vehicleId,
      serviceId,
      slotId,
      startTime,
      endTime,
      serviceDuration,
      priceCents,
      notes,
    });
    console.log("API_CREATE_BOOKING_RESULT", result);

    if (!result.data || !result.data.ok) {
      const errorMsg = result.data?.error?.message || "Unable to create booking.";
      console.error("API_ERROR_RESPONSE", result.data);
      throw new Error(errorMsg);
    }

    return result.data as BookingResponse;
  } catch (error: any) {
    console.error("API_CREATE_BOOKING_ERROR", error);
    throw error;
  }
}

// Get resident's upcoming bookings
async function getResidentBookings(): Promise<GetBookingsResponse> {
  const user = auth.currentUser;
  if (!user) {
    console.error("API_NO_CURRENT_USER");
    throw new Error("No authenticated Firebase user");
  }

  const token = await user.getIdToken(true);
  console.log("API_GET_BOOKINGS_START");

  try {
    const getBookingsFn = httpsCallable(functions, "getResidentBookings");
    const result: HttpsCallableResult<any> = await getBookingsFn();
    console.log("API_GET_BOOKINGS_RESULT", result);

    if (!result.data || !result.data.ok) {
      const errorMsg = result.data?.error?.message || "Unable to fetch bookings.";
      console.error("API_ERROR_RESPONSE", result.data);
      throw new Error(errorMsg);
    }

    return result.data as GetBookingsResponse;
  } catch (error: any) {
    console.error("API_GET_BOOKINGS_ERROR", error);
    throw error;
  }
}

// ==================== VALET REQUEST TYPES ====================
// Canonical types matching backend schema
export type ValetRequestType =
  | "vehicle_retrieval"
  | "early_departure"
  | "general_message"
  | "expected_visitor"
  | "ev_charge"
  | "special_instructions";

export type ValetRequestStatus = "pending" | "assigned" | "in_progress" | "completed" | "cancelled";
export type ValetRequestPriority = "low" | "normal" | "urgent";

export type ValetRequestWorkflowState =
  | "unassigned"
  | "accepted"
  | "delegated"
  | "started"
  | "done"
  | "cancelled";

export type ValetRequestChannel = "in_app" | "sms" | "email" | "phone";

// Canonical request document (from backend)
export interface ValetRequest {
  id: string;
  locationId: string;
  source: "residentPortal" | "commandCenter" | "androidValet" | "textbee";
  channel: ValetRequestChannel;
  requestType: ValetRequestType;
  message: string;
  residentId: string;
  residentName: string;
  unitNumber: string;
  phoneNumber: string;
  vehicleId: string | null;
  vehicleLabel: string | null;
  vehicleNotes: string;
  priority: ValetRequestPriority;
  requestedForAt: string;
  status: ValetRequestStatus;
  workflowState: ValetRequestWorkflowState;
  assignedToUserId: string | null;
  assignedToName: string | null;
  acceptedByUserId: string | null;
  replySent: boolean;
  overdueFlag: boolean;
  notes: string;
  internalNotes: string;
  createdAt: any;
  updatedAt: any;
  startedAt: string | null;
  completedAt: string | null;
  assignedAt: string | null;
  cancelledAt: string | null;
  acceptedAt: string | null;
}

// Priority label mapping for display
export const REQUEST_TYPE_LABELS: Record<ValetRequestType, string> = {
  vehicle_retrieval: "Vehicle Retrieval",
  early_departure: "Early Departure",
  general_message: "General Message",
  expected_visitor: "Expected Visitor",
  ev_charge: "EV Charge",
  special_instructions: "Special Instructions",
};

// Status label mapping for display
export const STATUS_LABELS: Record<ValetRequestStatus, string> = {
  pending: "Pending",
  assigned: "Assigned",
  in_progress: "In Progress",
  completed: "Completed",
  cancelled: "Cancelled",
};

// Request type icons
export const REQUEST_TYPE_ICONS: Record<ValetRequestType, string> = {
  vehicle_retrieval: "🚗",
  early_departure: "🚗",
  general_message: "🔧",
  expected_visitor: "👥",
  ev_charge: "⚡",
  special_instructions: "📝",
};

export interface ValetRequestPayload {
  requestType: ValetRequestType;
  message: string;
  vehicleId?: string;
  vehicleNotes?: string;
  requestedForAt?: string;
  priority?: ValetRequestPriority;
}

export interface ValetRequestResponse {
  ok: boolean;
  requestId?: string;
  status?: string;
  error?: {
    code: string;
    message: string;
  };
}

// Create a valet request
async function createValetRequest(
  payload: ValetRequestPayload
): Promise<ValetRequestResponse> {
  const user = auth.currentUser;
  if (!user) {
    console.error("API_NO_CURRENT_USER");
    throw new Error("No authenticated Firebase user");
  }

  const token = await user.getIdToken(true);
  console.log("API_CREATE_VALET_REQUEST_START", { payload });

  try {
    const createValetRequestFn = httpsCallable(functions, "createResidentValetRequest");
    const result: HttpsCallableResult<any> = await createValetRequestFn(payload);
    console.log("API_CREATE_VALET_REQUEST_RESULT", result);

    if (!result.data || !result.data.ok) {
      const errorMsg = result.data?.error?.message || "Unable to create valet request.";
      console.error("API_ERROR_RESPONSE", result.data);
      throw new Error(errorMsg);
    }

    return result.data as ValetRequestResponse;
  } catch (error: any) {
    console.error("API_CREATE_VALET_REQUEST_ERROR", error);
    throw error;
  }
}

// ==================== FEEDBACK TYPES ====================

export type FeedbackTag =
  | "friendly"
  | "fast"
  | "careful"
  | "professional"
  | "attentive";

export interface SubmitResidentValetFeedbackPayload {
  locationId: string;
  residentId: string;
  rating: number;                       // 1–5
  serviceType: string;                  // "retrieval" | "checkin" etc.
  source: "portal";
  checkinId?: string;
  valetRequestId?: string;
  tags?: FeedbackTag[];
  comment?: string;
}

export interface SubmitResidentValetFeedbackResponse {
  ok: boolean;
  feedbackId?: string;
  error?: {
    code: string;
    message: string;
  };
}

async function submitResidentValetFeedback(
  payload: SubmitResidentValetFeedbackPayload
): Promise<SubmitResidentValetFeedbackResponse> {
  const user = auth.currentUser;
  if (!user) {
    console.error("API_NO_CURRENT_USER");
    throw new Error("No authenticated Firebase user");
  }

  await user.getIdToken(true);
  console.log("API_SUBMIT_FEEDBACK_START", { serviceType: payload.serviceType, rating: payload.rating });

  try {
    const submitFn = httpsCallable(functions, "submitResidentValetFeedback");
    const result: HttpsCallableResult<any> = await submitFn(payload);
    console.log("API_SUBMIT_FEEDBACK_RESULT", result);

    if (!result.data || !result.data.ok) {
      const errorMsg = result.data?.error?.message || "Unable to submit feedback.";
      console.error("API_ERROR_RESPONSE", result.data);
      throw new Error(errorMsg);
    }

    return result.data as SubmitResidentValetFeedbackResponse;
  } catch (error: any) {
    console.error("API_SUBMIT_FEEDBACK_ERROR", error);
    throw error;
  }
}

// ==================== RESIDENT PORTAL SETTINGS ====================
//
// Canonical TypeScript shape for the per-location Resident Portal feature
// flags that gate what services the home screen exposes to a resident.
//
// Backend: `getResidentPortalSettings` callable (in
// functions/src/residentPortal/index.ts). The callable always returns a
// normalized shape — every nested object is present, every leaf is a
// boolean (or a clamped enum for provider labels). Provider SECRETS are
// never returned to this client.
//
// Source-of-truth Firestore path:
//   locations/{locationId}/settings/residentPortal
//
// Safe-by-default: missing fields default to `false`/`null`, so the portal
// never surfaces a service unless an admin has explicitly enabled it.
export interface ResidentPortalSettings {
  portalEnabled: boolean;
  allowResidentVehicleCreation: boolean;
  valetRequests: {
    enabled: boolean;
    allowRequestVehicle: boolean;
    allowHistory: boolean;
  };
  carWash: {
    enabled: boolean;
    allowBooking: boolean;
    requirePayment: boolean;
    paymentProvider: "stripe" | "square" | "manual" | null;
  };
  bookings: {
    enabled: boolean;
  };
  payments: {
    enabled: boolean;
    allowReceipts: boolean;
    provider: "stripe" | "square" | null;
  };
  evCharging: {
    enabled: boolean;
    allowResidentRequests: boolean;
  };
  guestParking: {
    enabled: boolean;
    allowBooking: boolean;
  };
  feedback?: {
    enabled: boolean;
    residentPortalEnabled: boolean;
  };
}

export interface GetResidentPortalSettingsResponse {
  ok: boolean;
  data?: ResidentPortalSettings;
  error?: { code: string; message: string };
}

/**
 * Fetch the Resident Portal settings for the caller's location. The backend
 * reads `locationId` from the caller's auth claims (token.locationId), so
 * this helper takes no argument — residents cannot query other buildings.
 *
 * Throws on network/auth failure. Caller may catch and fall back to a
 * conservative "no services" UI per the home-screen contract.
 */
async function getResidentPortalSettings(): Promise<GetResidentPortalSettingsResponse> {
  const user = auth.currentUser;
  if (!user) {
    console.error("API_NO_CURRENT_USER");
    throw new Error("No authenticated Firebase user");
  }

  // Force-refresh the ID token so the latest custom claims (role, locationId)
  // are sent — mirrors the pattern used by `getLocationFeatureSettings` above.
  await user.getIdToken(true);
  console.log("API_GET_RESIDENT_PORTAL_SETTINGS_START");

  try {
    const getFn = httpsCallable(functions, "getResidentPortalSettings");
    const result: HttpsCallableResult<any> = await getFn({});
    console.log("API_GET_RESIDENT_PORTAL_SETTINGS_RESULT", result);

    if (!result.data || !result.data.ok) {
      const errorMsg = result.data?.error?.message || "Unable to fetch portal settings.";
      console.error("API_ERROR_RESPONSE", result.data);
      throw new Error(errorMsg);
    }

    return result.data as GetResidentPortalSettingsResponse;
  } catch (error: any) {
    console.error("API_GET_RESIDENT_PORTAL_SETTINGS_ERROR", error);
    throw error;
  }
}

// ==================== FEATURE SETTINGS ====================

export interface LocationFeatureSettingsResponse {
  ok: boolean;
  data?: {
    staffEvaluation?: Record<string, unknown>;
    residentValetFeedback?: {
      enabled?: boolean;
      portalEnabled?: boolean;
      smsEnabled?: boolean;
      whatsappEnabled?: boolean;
      requireCompletedService?: boolean;
      allowComment?: boolean;
      allowTags?: boolean;
      allowEmployeeRating?: boolean;
      allowAnonymousResidentFeedback?: boolean;
      feedbackWindowHours?: number;
    };
  };
  error?: {
    code: string;
    message: string;
  };
}

async function getLocationFeatureSettings(
  locationId: string
): Promise<LocationFeatureSettingsResponse> {
  const user = auth.currentUser;
  if (!user) {
    console.error("API_NO_CURRENT_USER");
    throw new Error("No authenticated Firebase user");
  }

  await user.getIdToken(true);
  console.log("API_GET_FEATURE_SETTINGS_START", { locationId });

  try {
    const getFn = httpsCallable(functions, "getLocationFeatureSettings");
    const result: HttpsCallableResult<any> = await getFn({ locationId });
    console.log("API_GET_FEATURE_SETTINGS_RESULT", result);

    if (!result.data || !result.data.ok) {
      const errorMsg = result.data?.error?.message || "Unable to fetch feature settings.";
      console.error("API_ERROR_RESPONSE", result.data);
      throw new Error(errorMsg);
    }

    return result.data as LocationFeatureSettingsResponse;
  } catch (error: any) {
    console.error("API_GET_FEATURE_SETTINGS_ERROR", error);
    throw error;
  }
}

export const api = {
  createResidentVehicle,
  updateResidentVehicle,
  deleteResidentVehicle,
  getAvailableCarWashSlots,
  createCarWashBooking,
  getResidentBookings,
  createValetRequest,
  submitResidentValetFeedback,
  getLocationFeatureSettings,
  getResidentPortalSettings,
  submitResidentFeedback,
  resolveFeedbackPublicToken,
  submitFeedbackByToken,
  getPublicResidentParkingOnboarding,
};

export async function getPublicResidentParkingOnboarding(): Promise<ResidentParkingOnboarding> {
  const fn = httpsCallable(functions, "getPublicResidentParkingOnboarding");
  const result = await fn({ slug: "figueroa-eight" });
  const body = result.data as any;
  if (!body?.ok || !body.data || typeof body.data !== "object") throw new Error("Unable to load parking information.");
  return body.data as ResidentParkingOnboarding;
}

// ==================== RESIDENT FEEDBACK (FEEDBACK MODULE SLICE 1) ====================

export interface SubmitResidentFeedbackPayload {
  locationId: string;
  serviceType: string;
  message: string;
  rating?: number;
  vehicleId?: string;
}

export interface SubmitResidentFeedbackResponse {
  ok: boolean;
  feedbackId?: string;
  error?: { code: string; message: string };
}

async function submitResidentFeedback(
  payload: SubmitResidentFeedbackPayload
): Promise<SubmitResidentFeedbackResponse> {
  const user = auth.currentUser;
  if (!user) {
    console.error("API_NO_CURRENT_USER");
    throw new Error("No authenticated Firebase user");
  }

  await user.getIdToken(true);
  console.log("API_SUBMIT_RESIDENT_FEEDBACK_START", { serviceType: payload.serviceType });

  try {
    const fn = httpsCallable(functions, "submitResidentFeedback");
    const result: HttpsCallableResult<any> = await fn(payload);
    console.log("API_SUBMIT_RESIDENT_FEEDBACK_RESULT", result);

    if (!result.data || !result.data.ok) {
      const errorMsg = result.data?.error?.message || "Unable to submit feedback.";
      console.error("API_ERROR_RESPONSE", result.data);
      throw new Error(errorMsg);
    }

    return result.data as SubmitResidentFeedbackResponse;
  } catch (error: any) {
    console.error("API_SUBMIT_RESIDENT_FEEDBACK_ERROR", error);
    throw error;
  }
}

// ==================== PRODUCTION GUEST FEEDBACK TOKEN PATH ====================
// The raw token is delivered to the guest via QR / SMS / email and never
// traces back to a locationId on the client; the backend resolves the token
// to a location server-side.
//
// SECURITY:
//   - Never send locationId or organizationId in these payloads — the
//     resolver derives them from the token doc itself.
//   - Never log tenant identifiers here. The public success/failure paths
//     return a single neutral shape regardless of token state so callers
//     cannot enumerate valid tokens.

export interface ResolveFeedbackPublicTokenPayload {
  token: string;
}

export interface ResolveFeedbackPublicTokenServiceOption {
  id:    string;
  label: string;
}

export interface ResolveFeedbackPublicTokenResponse {
  ok:                   boolean;
  serviceTypeDefault?:  string;
  requireRating:        boolean;
  allowAnonymousGuest:  boolean;
  brandName?:           string;
  logoUrl?:             string;
  formTitle?:           string;
  formSubtitle?:        string;
  showPoweredByAtrium:  boolean;
  publicReviewerLabel:  string;
  publicResponseCommitmentEnabled: boolean;
  publicResponseTargetHours?: number;
  publicResponseRequestEnabled: boolean;
  publicResponseContactMethods: Array<"sms" | "phone" | "email">;
  serviceQuestionEnabled:  boolean;
  serviceQuestionRequired: boolean;
  serviceOptions:          ResolveFeedbackPublicTokenServiceOption[];
  error?: { code: string; message: string };
}

async function resolveFeedbackPublicToken(
  payload: ResolveFeedbackPublicTokenPayload
): Promise<ResolveFeedbackPublicTokenResponse> {
  // Public, unauthenticated. No tenant identifiers leave the client.
  console.log("API_RESOLVE_FEEDBACK_PUBLIC_TOKEN_START");
  try {
    const fn = httpsCallable(functions, "resolveFeedbackPublicToken");
    const result: HttpsCallableResult<any> = await fn(payload);

    if (!result.data || result.data.ok !== true) {
      const errorMsg = result.data?.error?.message || "Token not resolvable.";
      console.warn("API_RESOLVE_FEEDBACK_PUBLIC_TOKEN_ERROR_RESPONSE", { ok: result.data?.ok });
      throw new Error(errorMsg);
    }

    // Whitelist: only the safe form-driver fields are propagated. Anything
    // unexpected that may slip into the response is dropped here.
    const rawOptions = Array.isArray(result.data.serviceOptions) ? result.data.serviceOptions : [];
    return {
      ok:                  true,
      serviceTypeDefault:  typeof result.data.serviceTypeDefault === "string"
        ? result.data.serviceTypeDefault
        : undefined,
      requireRating:       result.data.requireRating === true,
      allowAnonymousGuest: result.data.allowAnonymousGuest === true,
      brandName:           typeof result.data.brandName    === "string" ? result.data.brandName    : undefined,
      logoUrl:             typeof result.data.logoUrl      === "string" ? result.data.logoUrl      : undefined,
      formTitle:           typeof result.data.formTitle    === "string" ? result.data.formTitle    : undefined,
      formSubtitle:        typeof result.data.formSubtitle === "string" ? result.data.formSubtitle : undefined,
      showPoweredByAtrium: result.data.showPoweredByAtrium !== false,
      publicReviewerLabel: typeof result.data.publicReviewerLabel === "string" && result.data.publicReviewerLabel.trim()
        ? result.data.publicReviewerLabel.trim()
        : "the service team",
      publicResponseCommitmentEnabled: result.data.publicResponseCommitmentEnabled === true,
      publicResponseTargetHours: typeof result.data.publicResponseTargetHours === "number"
        && Number.isInteger(result.data.publicResponseTargetHours)
        && result.data.publicResponseTargetHours >= 1 && result.data.publicResponseTargetHours <= 168
        ? result.data.publicResponseTargetHours : undefined,
      publicResponseRequestEnabled: result.data.publicResponseRequestEnabled !== false,
      publicResponseContactMethods: (Array.isArray(result.data.publicResponseContactMethods) ? result.data.publicResponseContactMethods : [])
        .filter((m: any): m is "sms" | "phone" | "email" => m === "sms" || m === "phone" || m === "email")
        .filter((m: "sms" | "phone" | "email", i: number, a: Array<"sms" | "phone" | "email">) => a.indexOf(m) === i),
      serviceQuestionEnabled:  result.data.serviceQuestionEnabled !== false,
      serviceQuestionRequired: result.data.serviceQuestionRequired === true,
      serviceOptions: rawOptions
        .filter((o: any) => o && typeof o.id === "string" && typeof o.label === "string")
        .map((o: any) => ({ id: o.id, label: o.label })),
    };
  } catch (error: any) {
    console.error("API_RESOLVE_FEEDBACK_PUBLIC_TOKEN_ERROR", { code: error?.code ?? null });
    throw error;
  }
}

export interface SubmitFeedbackByTokenPayload {
  token:           string;
  serviceType:     string;
  rating?:         number;
  message:         string;
  contactName?:    string;
  contactEmail?:   string;
  contactPhone?:   string;
  responseRequested?: boolean;
  preferredContactMethod?: "sms" | "phone" | "email";
  ticketOrPlate?:  string;
}

export interface SubmitFeedbackByTokenResponse {
  ok: boolean;
  // Human-readable ticket code (e.g. "FB-2026-000123"), safe and intended to
  // be shown to the guest — distinct from internal identifiers, which are
  // never echoed to the client.
  referenceNumber?: string;
  error?: { code: string; message: string };
}

async function submitFeedbackByToken(
  payload: SubmitFeedbackByTokenPayload
): Promise<SubmitFeedbackByTokenResponse> {
  // Public, unauthenticated. The payload deliberately does NOT carry
  // locationId or organizationId — the backend derives them from the token.
  console.log("API_SUBMIT_FEEDBACK_BY_TOKEN_START", { serviceType: payload.serviceType });
  try {
    const fn = httpsCallable(functions, "submitFeedbackByToken");
    const result: HttpsCallableResult<any> = await fn(payload);

    if (!result.data || result.data.ok !== true) {
      const errorMsg = result.data?.error?.message || "Unable to submit feedback.";
      console.error("API_SUBMIT_FEEDBACK_BY_TOKEN_ERROR_RESPONSE", { ok: result.data?.ok });
      throw new Error(errorMsg);
    }

    // Pass through the human-readable reference number only — internal
    // identifiers are still never echoed to the client.
    return {
      ok: true,
      referenceNumber: typeof result.data.referenceNumber === "string" ? result.data.referenceNumber : undefined,
    };
  } catch (error: any) {
    console.error("API_SUBMIT_FEEDBACK_BY_TOKEN_ERROR", { code: error?.code ?? null });
    throw error;
  }
}
